import { useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Box from '@mui/material/Box';
import DialogActionsBar from '../shared/DialogActionsBar';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  /** Bold name/label shown on its own line to highlight what will be affected */
  itemName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Color scheme for the confirm button + question-mark icon:
   *  'danger' = red, 'warning' = orange, 'info' = blue. */
  variant?: 'danger' | 'warning' | 'info';
  /** Disables confirm button while operation is in progress */
  isPending?: boolean;
  /** The confirm button's text while `isPending`, e.g. 'Deleting...'. Defaults to 'Working...'. */
  busyText?: string;
  /**
   * Which button is initially focused / responds to Enter, for a prompt that is
   * not destructive ('warning' / 'info').
   * 'confirm' (default) matches WinForms `dmQuestion` (DFB1: Yes wins): Enter confirms.
   * 'cancel' matches WinForms `dmQuestion_DFB2` (DFB2: No wins — used
   * for "are you absolutely sure" reconfirmation prompts): Enter cancels.
   * A destructive prompt (variant 'danger') always starts on Cancel and Enter never confirms it.
   */
  defaultFocusButton?: 'confirm' | 'cancel';
}

/**
 * Reusable confirmation dialog for destructive and important actions.
 *
 * Usage:
 * ```tsx
 * <ConfirmDialog
 *   open={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Resident"
 *   message="This action cannot be undone."
 *   itemName={resident.fullName}
 *   variant="danger"
 * />
 * ```
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm',
  message = 'Are you sure you want to proceed?',
  itemName,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isPending = false,
  busyText = 'Working...',
  defaultFocusButton = 'confirm',
}: ConfirmDialogProps) {
  const colorMap = {
    danger: 'error' as const,
    warning: 'warning' as const,
    info: 'primary' as const,
  };
  const muiColor = colorMap[variant];

  const iconColorMap = {
    danger: 'var(--color-negative, #d32f2f)',
    warning: 'var(--color-warning, #ed6c02)',
    info: 'var(--primary-blue)',
  };

  /* Jeff, 2026-10-21: a destructive prompt (variant 'danger') starts on Cancel and Enter never
     confirms it (Enter on the focused Cancel cancels). Other prompts start where
     `defaultFocusButton` says; on 'confirm', Enter confirms. Esc cancels (the Dialog's onClose). */
  const destructive = variant === 'danger';
  const focusCancel = destructive || defaultFocusButton === 'cancel';

  /* The button that should hold the focus once the dialog has opened. `autoFocus` alone lost it in the
     browser: the dialog's focus trap moved focus to the dialog box itself, so Cancel was not focused and
     Enter / Space did nothing (found by the Menu Cycle Workbench browser check, 2026-09-26). */
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const focusDefault = useCallback(() => {
    (focusCancel ? cancelRef : confirmRef).current?.focus();
  }, [focusCancel]);

  const handleConfirm = useCallback(() => {
    if (!isPending) onConfirm();
  }, [isPending, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      slotProps={{ transition: { onEntered: focusDefault } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {/* All variants use the same question-mark glyph — the
            dialog is fundamentally a confirmation prompt. Color
            still varies by variant so destructive prompts read
            red, warnings orange, and informational blue. */}
        <HelpOutlineIcon sx={{ color: iconColorMap[variant], fontSize: 24 }} />
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {message}
        </Typography>
        {itemName && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: variant === 'danger' ? 'error.50' : 'action.hover',
              border: '1px solid',
              borderColor: variant === 'danger' ? 'error.200' : 'divider',
            }}
          >
            <Typography
              variant="body2"
              // pre-line: a list of names (one per line) shows as a list.
              sx={{ fontWeight: 600, color: 'text.primary', wordBreak: 'break-word', whiteSpace: 'pre-line' }}
            >
              {itemName}
            </Typography>
          </Box>
        )}
      </DialogContent>
      {/* Jeff's rule (docs/ui/dialog-actions.md): Cancel, then the confirming action at the far right,
          the only filled button (red for a destructive prompt). The shared bar gives the order, the
          Left / Right keys between the buttons and Enter / Esc. */}
      <DialogActionsBar
        className="confirm-dialog-actions"
        onCancel={onClose}
        cancelLabel={cancelLabel}
        onSave={handleConfirm}
        saveLabel={confirmLabel}
        savingLabel={busyText}
        saving={isPending}
        saveColor={muiColor}
        /* Enter confirms only a prompt that starts on the confirming button. */
        enterSaves={!focusCancel}
        initialFocus={focusCancel ? 'cancel' : 'save'}
        cancelRef={cancelRef}
        saveRef={confirmRef}
        sx={{ px: 3, pb: 2 }}
      />
    </Dialog>
  );
}
